import React from "react";
import Chapter from "@/components/landing/Chapter";
import Reveal from "@/components/landing/Reveal";
import { useLandingImages } from "@/lib/ImagesContext";

/**
 * Gallery — displays user-uploaded gallery photos in an editorial grid.
 *
 * Only renders when at least one photo exists in the "gallery" slot. If
 * empty, the section is skipped entirely so the landing stays clean.
 */
const Gallery = () => {
  const { gallery } = useLandingImages();

  if (!gallery || gallery.length === 0) return null;

  const total = gallery.length;

  return (
    <section
      id="galeria"
      data-testid="gallery-section"
      className="bg-[#F4EFE7]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 lg:py-32">
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-12 md:mb-16">
          <div className="col-span-12 md:col-span-7">
            <Reveal>
              <Chapter number="03" label="Galería" />
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.05] text-[#3F403A]">
                Instantes que
                <span className="italic text-[#63846F]"> quedan.</span>
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 md:col-span-5">
            <Reveal delay={140}>
              <p className="font-sans text-base md:text-lg leading-relaxed text-[#3F403A]/80">
                Momentos reales de ediciones anteriores — networking, ponencias,
                complicidad y decisiones tomadas en la sala.
              </p>
            </Reveal>
          </div>
        </div>

        <div
          className={`grid gap-4 md:gap-6 grid-cols-2 ${
            total <= 2
              ? "md:grid-cols-2"
              : total === 3
                ? "md:grid-cols-3"
                : "md:grid-cols-3 lg:grid-cols-4"
          }`}
          data-testid="gallery-grid"
        >
          {gallery.map((img, i) => {
            // Alternate aspect ratios for visual rhythm.
            const ratios = ["3/4", "1/1", "3/4", "4/5"];
            const ratio = ratios[i % ratios.length];
            return (
              <Reveal
                key={img.id}
                delay={Math.min(i * 60, 400)}
                className="relative overflow-hidden bg-[#FAF8F3] group"
              >
                <div
                  className="relative overflow-hidden"
                  style={{ aspectRatio: ratio }}
                  data-testid={`gallery-item-${i}`}
                >
                  <img
                    src={img.url}
                    alt={img.filename || `Comunidad Lidera con Voz Propia ${i + 1}`}
                    className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.05]"
                    loading="lazy"
                  />
                  <div className="pointer-events-none absolute inset-0 border border-[#3F403A]/10" />
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={200}>
          <p className="mt-10 md:mt-12 overline text-[#3F403A]/60 text-center">
            {total} {total === 1 ? "imagen" : "imágenes"} · ediciones anteriores
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default Gallery;
